import _ from "lodash";
import React from "react";
import * as Yup from "yup";

import LoginBackgroundType from "./LoginBackgroundType";
import SidebarCssVariable from "@/containers/Theme/SidebarCssVariable";
import GlobalUtils from "@/utils/GlobalUtils";
import { SntColor } from "@/cleanup/constants/SntColor";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

export interface CustomInfo {
  id?: number | null;
  name?: string | null;
  fromEmailAddress?: string | null;
  fromEmailAlias?: string | null;
  emailVerificationStatus?: string | null;
  loginTitle?: string | null;
  baseColor?: string;
  loginButtonColor?: string;
  headerColor?: string;
  homeLogoPath?: string | null;
  homeSideBarColor?: string;
  homeSideBarSectionFontColor?: string;
  homeSideBarFontColor?: string;
  homeSideBarSelectedColor?: string;
  homeFooterBackgroundColor?: string;
  homeFooterButtonSelectedColor?: string;
  adminLogoPath?: string | null;
  favIconPath?: string | null;
  adminSideBarColor?: string;
  adminSideBarSectionFontColor?: string;
  adminSideBarFontColor?: string;
  adminSideBarSelectedColor?: string;
  adminFooterBackgroundColor?: string;
  adminFooterButtonSelectedColor?: string;
  loginBackgroundType?: string;
  loginBackgroundPosition?: string;
  homeLogoImgSrc?: string | null;
  faviconImgSrc?: string | null;
  adminLogoImgSrc?: string | null;
  bigLogoLiveSrc?: string | null;
  backgroundFileLiveSrc?: string | null;
  reportLogoLiveSrc?: string | null;
  emailLogoLiveSrc?: string | null;
  customType?: string;
  customId?: string | number;
  iconPath?: string;
  domain?: string;
  bigLogoIconPath?: string;
  loginBackgroundImgPath?: string;
  reportLogoImgPath?: string;
  emailLogoImgPath?: string;
  customIphoneApp?: string;
  customAndroidApp?: string;
  file?: File | null;
  file1?: File | null;
  file2?: File | null;
  file3?: File | null;
  file4?: File | null;
  file5?: File | null;
  file6?: File | null;
}

export interface OrganisationData {
  id?: number;
  organisationType?: string;
  partner?: {
    id?: number;
    externalId?: string;
  };
}

interface DefaultSideBar {
  homeSideBarColor: string;
  homeSideBarSectionFontColor: string;
  homeSideBarFontColor: string;
  homeSideBarSelectedColor: string;
  homeFooterBackgroundColor: string;
  homeFooterButtonSelectedColor: string;
  adminSideBarColor: string;
  adminSideBarSectionFontColor: string;
  adminSideBarFontColor: string;
  adminSideBarSelectedColor: string;
  adminFooterBackgroundColor: string;
  adminFooterButtonSelectedColor: string;
  file: File | null;
  homeLogoPath: string;
  favIconPath: string;
  adminLogoPath: string;
}

interface HomeSideBar {
  homeSideBarColor: string;
  homeSideBarSectionFontColor: string;
  homeSideBarFontColor: string;
  homeSideBarSelectedColor: string;
  homeFooterBackgroundColor: string;
  homeFooterButtonSelectedColor: string;
  file: File | null;
  homeLogoPath: string;
  customId: string;
  customType: string;
}

interface AdminSideBar {
  adminSideBarColor: string;
  adminSideBarSectionFontColor: string;
  adminSideBarFontColor: string;
  adminSideBarSelectedColor: string;
  adminFooterBackgroundColor: string;
  adminFooterButtonSelectedColor: string;
  file: File | null;
  adminLogoPath: string;
  customId: string;
  customType: string;
}

interface DefaultEmail {
  name: string;
  file4: File | null;
  emailLogoImgPath: string;
  headerColor: string;
}

interface DefaultLoginPage {
  loginBackgroundType: string;
  loginBackgroundPosition: string;
  baseColor: string;
  loginTitle: string;
  loginButtonColor: string;
  file1: File | null;
  bigLogoIconPath: string;
  file2: File | null;
  loginBackgroundImgPath: string;
}

interface ImageFileInput {
  file: File;
}

export const useCustomBranding = () => {

  const append = (key: string, value: string | number | null | undefined, formData: FormData): void => {
    if (!isNullOrUndefined(value)) {
      formData.append(key, String(value));
    }
  };

  const isNullOrUndefined = (value: unknown): boolean => {
    return _.isNull(value) || _.isUndefined(value);
  };

  const getDefaultSideBar = (): DefaultSideBar => {
    const homeSideBar = getDefaultHomeSideBar();
    const adminSideBar = getDefaultAdminSideBar();
    return {
      ...homeSideBar,
      ...adminSideBar,
      file: null,
      homeLogoPath: "",
      favIconPath: "",
      adminLogoPath: "",
    };
  };

  const getDefaultHomeSideBar = (): HomeSideBar => {
    return {
      homeSideBarColor: SidebarCssVariable.homeSideBarColorDefault,
      homeSideBarSectionFontColor:
        SidebarCssVariable.homeSideBarSectionFontColorDefault,
      homeSideBarFontColor: SidebarCssVariable.homeSideBarFontColorDefault,
      homeSideBarSelectedColor:
        SidebarCssVariable.homeSideBarSelectedColorDefault,
      homeFooterBackgroundColor:
        SidebarCssVariable.homeFooterBackgroundColorDefault,
      homeFooterButtonSelectedColor:
        SidebarCssVariable.homeFooterButtonSelectedColorDefault,
      file: null,
      homeLogoPath: "",
      customId: "",
      customType: "",
    };
  };

  const getEmptyLogoOnHeaderBar = (): { file: null; homeLogoPath: string } => {
    return {
      file: null,
      homeLogoPath: "",
    };
  };

  const getEmptyFavicon = (): { file5: null; favIconPath: string } => {
    return {
      file5: null,
      favIconPath: "",
    };
  };

  const getDefaultAdminSideBar = (): AdminSideBar => {
    return {
      adminSideBarColor: SidebarCssVariable.adminSideBarColorDefault,
      adminSideBarSectionFontColor:
        SidebarCssVariable.adminSideBarSectionFontColorDefault,
      adminSideBarFontColor: SidebarCssVariable.adminSideBarFontColorDefault,
      adminSideBarSelectedColor:
        SidebarCssVariable.adminSideBarSelectedColorDefault,
      adminFooterBackgroundColor:
        SidebarCssVariable.adminFooterBackgroundColorDefault,
      adminFooterButtonSelectedColor:
        SidebarCssVariable.adminFooterButtonSelectedColorDefault,
      file: null,
      adminLogoPath: "",
      customId: "",
      customType: "",
    };
  };

  const getEmptyAdminLogo = (): { file6: null; adminLogoPath: string } => {
    return {
      file6: null,
      adminLogoPath: "",
    };
  };

  const getDefaultEmail = (): DefaultEmail => {
    return {
      name: "",
      file4: null,
      emailLogoImgPath: "",
      headerColor: "#212851", // email color
    };
  };

  const getEmptyLogoInEmail = (): { file4: File | null; emailLogoImgPath: string } => {
    const defaultEmail = getDefaultEmail();
    return {
      file4: defaultEmail.file4,
      emailLogoImgPath: defaultEmail.emailLogoImgPath,
    };
  };

  const getEmptyReportLogo = (): { file3: null; reportLogoImgPath: string } => {
    return {
      file3: null,
      reportLogoImgPath: "",
    };
  };

  // use for Formik
  const getDefaultCustomInfo = (): CustomInfo => {
    const defaultSideBar = getDefaultSideBar();
    return {
      name: null,
      fromEmailAddress: null,
      fromEmailAlias: null,
      emailVerificationStatus: null,
      loginTitle: null,
      baseColor: "#212851",
      loginButtonColor: "#304269",
      headerColor: "#212851", // email color
      homeLogoPath: null,
      homeSideBarColor: defaultSideBar.homeSideBarColor,
      homeSideBarSectionFontColor: defaultSideBar.homeSideBarSectionFontColor,
      homeSideBarFontColor: defaultSideBar.homeSideBarFontColor,
      homeSideBarSelectedColor: defaultSideBar.homeSideBarSelectedColor,
      homeFooterBackgroundColor: defaultSideBar.homeFooterBackgroundColor,
      homeFooterButtonSelectedColor:
        defaultSideBar.homeFooterButtonSelectedColor,
      adminLogoPath: null,
      favIconPath: null,
      adminSideBarColor: defaultSideBar.adminSideBarColor,
      adminSideBarSectionFontColor: defaultSideBar.adminSideBarSectionFontColor,
      adminSideBarFontColor: defaultSideBar.adminSideBarFontColor,
      adminSideBarSelectedColor: defaultSideBar.adminSideBarSelectedColor,
      adminFooterBackgroundColor: defaultSideBar.adminFooterBackgroundColor,
      adminFooterButtonSelectedColor:
        defaultSideBar.adminFooterButtonSelectedColor,

      loginBackgroundType: LoginBackgroundType.image,
      loginBackgroundPosition: "cover",

      // for logic
      homeLogoImgSrc: null,
      faviconImgSrc: null,
      adminLogoImgSrc: null,
      bigLogoLiveSrc: null,
      backgroundFileLiveSrc: null,
      reportLogoLiveSrc: null,
      emailLogoLiveSrc: null,
    };
  };

  const mergeDataWithValueDefault = (data: CustomInfo): CustomInfo => {
    const defaultSideBar = getDefaultSideBar();

    const mergeWithDefault = {
      baseColor: data.baseColor || "#212851",
      loginButtonColor: data.loginButtonColor || "#304269",
      headerColor: data.headerColor || "#212851", // email color
      homeSideBarColor:
        data.homeSideBarColor || defaultSideBar.homeSideBarColor,
      homeSideBarSectionFontColor:
        data.homeSideBarSectionFontColor ||
        defaultSideBar.homeSideBarSectionFontColor,
      homeSideBarFontColor:
        data.homeSideBarFontColor || defaultSideBar.homeSideBarFontColor,
      homeSideBarSelectedColor:
        data.homeSideBarSelectedColor ||
        defaultSideBar.homeSideBarSelectedColor,
      homeFooterBackgroundColor:
        data.homeFooterBackgroundColor ||
        defaultSideBar.homeFooterBackgroundColor,
      homeFooterButtonSelectedColor:
        data.homeFooterButtonSelectedColor ||
        defaultSideBar.homeFooterButtonSelectedColor,
      adminSideBarColor:
        data.adminSideBarColor || defaultSideBar.adminSideBarColor,
      adminSideBarSectionFontColor:
        data.adminSideBarSectionFontColor ||
        defaultSideBar.adminSideBarSectionFontColor,
      adminSideBarFontColor:
        data.adminSideBarFontColor || defaultSideBar.adminSideBarFontColor,
      adminSideBarSelectedColor:
        data.adminSideBarSelectedColor ||
        defaultSideBar.adminSideBarSelectedColor,
      adminFooterBackgroundColor:
        data.adminFooterBackgroundColor ||
        defaultSideBar.adminFooterBackgroundColor,
      adminFooterButtonSelectedColor:
        data.adminFooterButtonSelectedColor ||
        defaultSideBar.adminFooterButtonSelectedColor,
    };

    return { ...data, ...mergeWithDefault };
  };

  const onChangeImg = (image: ImageFileInput, setSrc: (src: string[]) => void): void => {
    // Assuming only image
    const file = image.file;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = function () {
      setSrc([reader.result as string]);
    };
  };

  const validationSchema = Yup.object().shape({
    domain: Yup.string()
      .nullable()
      .matches(
        /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
        translate(LanguageKey.please_enter_the_correct_url_key)
      ),
    customIphoneApp: Yup.string()
      .url()
      .required(translate(LanguageKey.valid_required_key))
      .nullable()
      .notRequired(),
    customAndroidApp: Yup.string()
      .url()
      .required(translate(LanguageKey.valid_required_key))
      .nullable()
      .notRequired(),
  });

  const customValuesToFormData = (initialValues: CustomInfo, orgData: OrganisationData): FormData => {
    const formData = new FormData();
    append(
      "domain",
      initialValues.domain
        ? initialValues.domain.replace(/((^\w+:|^)\/\/)?(www.)/, "")
        : initialValues.domain,
      formData
    );
    append("name", initialValues.name, formData);
    append("fromEmailAddress", initialValues.fromEmailAddress, formData);
    append("fromEmailAlias", initialValues.fromEmailAlias, formData);
    append("loginTitle", initialValues.loginTitle, formData);

    if (initialValues.file) {
      // clear old path and append new file
      append("homeLogoPath", "", formData);
      formData.append(
        "homeLogoFile",
        initialValues.file,
        initialValues.file.name
      );
    } else {
      append("homeLogoPath", initialValues.homeLogoPath, formData);
    }

    if (initialValues.file1) {
      // clear old path and append new file
      append("bigLogoIconPath", "", formData);
      formData.append(
        "bigLogoFile",
        initialValues.file1,
        initialValues.file1.name
      );
    } else {
      append("bigLogoIconPath", initialValues.bigLogoIconPath, formData);
    }

    if (initialValues.file2) {
      // clear old path and append new file
      append("loginBackgroundImgPath", "", formData);
      formData.append(
        "loginBackgroundFile",
        initialValues.file2,
        initialValues.file2.name
      );
    } else {
      append(
        "loginBackgroundImgPath",
        initialValues.loginBackgroundImgPath,
        formData
      );
    }

    if (initialValues.file3) {
      // clear old path and append new file
      append("reportLogoImgPath", "", formData);
      formData.append(
        "reportLogoFile",
        initialValues.file3,
        initialValues.file3.name
      );
    } else {
      append("reportLogoImgPath", initialValues.reportLogoImgPath, formData);
    }

    if (initialValues.file4) {
      // clear old path and append new file
      append("emailLogoImgPath", "", formData);
      formData.append(
        "emailLogoFile",
        initialValues.file4,
        initialValues.file4.name
      );
    } else {
      append("emailLogoImgPath", initialValues.emailLogoImgPath, formData);
    }

    if (initialValues.file5) {
      // clear old path and append new file
      append("favIconPath", "", formData);
      formData.append(
        "favIconFile",
        initialValues.file5,
        initialValues.file5.name
      );
    } else {
      append("favIconPath", initialValues.favIconPath, formData);
    }

    if (initialValues.file6) {
      // clear old path and append new file
      append("adminLogoPath", "", formData);
      formData.append(
        "adminLogoFile",
        initialValues.file6,
        initialValues.file6.name
      );
    } else {
      append("adminLogoPath", initialValues.adminLogoPath, formData);
    }

    let customId: number | null = null;
    let customType: string | null = null;
    if (orgData && orgData.organisationType === "PARTNER" && orgData.partner) {
      customId = orgData.partner.id ?? null;
      customType = "partner";
    } else {
      customId = orgData.id ?? null;
      customType = "organisation";
    }
    append("customId", customId, formData);
    append("customType", customType, formData);
    append("headerColor", initialValues.headerColor, formData); // email color
    append("baseColor", initialValues.baseColor, formData);
    append("loginButtonColor", initialValues.loginButtonColor, formData);

    append("homeSideBarColor", initialValues.homeSideBarColor, formData);
    append(
      "homeSideBarSectionFontColor",
      initialValues.homeSideBarSectionFontColor,
      formData
    );
    append(
      "homeSideBarFontColor",
      initialValues.homeSideBarFontColor,
      formData
    );
    append(
      "homeSideBarSelectedColor",
      initialValues.homeSideBarSelectedColor,
      formData
    );
    append(
      "homeFooterBackgroundColor",
      initialValues.homeFooterBackgroundColor,
      formData
    );
    append(
      "homeFooterButtonSelectedColor",
      initialValues.homeFooterButtonSelectedColor,
      formData
    );

    append("adminSideBarColor", initialValues.adminSideBarColor, formData);
    append(
      "adminSideBarSectionFontColor",
      initialValues.adminSideBarSectionFontColor,
      formData
    );
    append(
      "adminSideBarFontColor",
      initialValues.adminSideBarFontColor,
      formData
    );
    append(
      "adminSideBarSelectedColor",
      initialValues.adminSideBarSelectedColor,
      formData
    );
    append(
      "adminFooterBackgroundColor",
      initialValues.adminFooterBackgroundColor,
      formData
    );
    append(
      "adminFooterButtonSelectedColor",
      initialValues.adminFooterButtonSelectedColor,
      formData
    );

    append("loginBackgroundType", initialValues.loginBackgroundType, formData);
    append(
      "loginBackgroundPosition",
      initialValues.loginBackgroundPosition,
      formData
    );

    append("customIphoneApp", initialValues.customIphoneApp, formData);
    append("customAndroidApp", initialValues.customAndroidApp, formData);

    if (formData.get("baseColor") === "#75b9e6") {
      formData.set("baseColor", "");
    }

    if (formData.get("loginButtonColor") === "#304269") {
      formData.set("loginButtonColor", "");
    }

    // Persist a color only when it differs from its default; a color left at the
    // default is treated as "not set" (stored empty/null) so it inherits per field
    // org -> partner -> system default at render time.
    const defaults = getDefaultCustomInfo();
    const colorFields: (keyof CustomInfo)[] = [
      "baseColor",
      "loginButtonColor",
      "headerColor",
      "homeSideBarColor",
      "homeSideBarSectionFontColor",
      "homeSideBarFontColor",
      "homeSideBarSelectedColor",
      "homeFooterBackgroundColor",
      "homeFooterButtonSelectedColor",
      "adminSideBarColor",
      "adminSideBarSectionFontColor",
      "adminSideBarFontColor",
      "adminSideBarSelectedColor",
      "adminFooterBackgroundColor",
      "adminFooterButtonSelectedColor",
    ];
    colorFields.forEach((field) => {
      if (formData.get(field) === defaults[field]) {
        formData.set(field, "");
      }
    });

    return formData;
  };

  const renderImg = (src: string, heightInPx?: number, _marginTopPx?: number): React.ReactNode => {
    const size = heightInPx || 16;
    // Checkerboard backing + aspect-preserving image so the logo is visible
    // regardless of its own color: white/transparent logos are designed for the
    // colored header bar and would otherwise vanish on the white branding page.
    // maxHeight/maxWidth keep the real aspect ratio instead of squashing the
    // logo into a square.
    const checker = SntColor.Sensolus_Grey_Light;
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "3px 5px",
          borderRadius: 4,
          border: `1px solid ${SntColor.Sensolus_Grey_Light}`,
          backgroundColor: SntColor.White,
          backgroundImage: `linear-gradient(45deg, ${checker} 25%, transparent 25%), linear-gradient(-45deg, ${checker} 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${checker} 75%), linear-gradient(-45deg, transparent 75%, ${checker} 75%)`,
          backgroundSize: "8px 8px",
          backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0",
          verticalAlign: "middle",
        }}
      >
        <img
          style={{ maxHeight: size, maxWidth: 120, display: "block" }}
          src={src}
          alt=""
        />
      </span>
    );
  };

  const getBackgroundFileLiveSrc = (customInfo: CustomInfo): string | null => {
    let src: string | null = null;
    if (
      customInfo.customType &&
      customInfo.customId &&
      customInfo.loginBackgroundImgPath
    ) {
      src = GlobalUtils.getFile(
        customInfo.customType,
        customInfo.customId,
        "background",
        customInfo.loginBackgroundImgPath
      );
    }
    return src;
  };

  const getBigLogoLiveSrc = (customInfo: CustomInfo): string | null => {
    let src: string | null = null;
    if (
      customInfo.customType &&
      customInfo.customId &&
      customInfo.bigLogoIconPath
    ) {
      src = GlobalUtils.getFile(
        customInfo.customType,
        customInfo.customId,
        "big_logo",
        customInfo.bigLogoIconPath
      );
    }
    return src;
  };

  const getHomeLogoImgSrc = (customInfo: CustomInfo): string | null => {
    let src: string | null = null;
    if (
      customInfo.customType &&
      customInfo.customId &&
      customInfo.homeLogoPath
    ) {
      src = GlobalUtils.getFile(
        customInfo.customType,
        customInfo.customId,
        "logo",
        customInfo.homeLogoPath
      );
    }
    return src;
  };

  const getFaviconImgSrc = (customInfo: CustomInfo): string | null => {
    let src: string | null = null;
    if (
      customInfo.customType &&
      customInfo.customId &&
      customInfo.favIconPath
    ) {
      src = GlobalUtils.getFile(
        customInfo.customType,
        customInfo.customId,
        "favicon",
        customInfo.favIconPath
      );
    }
    return src;
  };

  const getAdminLogoImgSrc = (customInfo: CustomInfo): string | null => {
    let src: string | null = null;
    if (
      customInfo.customType &&
      customInfo.customId &&
      customInfo.adminLogoPath
    ) {
      src = GlobalUtils.getFile(
        customInfo.customType,
        customInfo.customId,
        "admin_logo",
        customInfo.adminLogoPath
      );
    }
    return src;
  };

  const getDefaultLoginPage = (): DefaultLoginPage => {
    return {
      loginBackgroundType: LoginBackgroundType.image,
      loginBackgroundPosition: "cover",
      baseColor: getDefaultCustomInfo().baseColor || "#212851",
      loginTitle: "",
      loginButtonColor: getDefaultCustomInfo().loginButtonColor || "#304269",
      file1: null,
      bigLogoIconPath: "",
      file2: null,
      loginBackgroundImgPath: "",
    };
  };

  const getEmptyLogoLoginImageMap = (): { file1: File | null; bigLogoIconPath: string } => {
    const defaultLogin = getDefaultLoginPage();
    return {
      file1: defaultLogin.file1,
      bigLogoIconPath: defaultLogin.bigLogoIconPath,
    };
  };

  const getEmptyBackgroundLoginImageMap = (): { file2: File | null; loginBackgroundImgPath: string } => {
    const defaultLogin = getDefaultLoginPage();
    return {
      file2: defaultLogin.file2,
      loginBackgroundImgPath: defaultLogin.loginBackgroundImgPath,
    };
  };

  const getReportLogoLiveSrc = (customInfo: CustomInfo): string | null => {
    let src: string | null = null;
    if (
      customInfo.customType &&
      customInfo.customId &&
      customInfo.reportLogoImgPath
    ) {
      src = GlobalUtils.getFile(
        customInfo.customType,
        customInfo.customId,
        "report_logo",
        customInfo.reportLogoImgPath
      );
    }
    return src;
  };

  const getEmailLogoLiveSrc = (customInfo: CustomInfo): string | null => {
    let src: string | null = null;
    if (
      customInfo.customType &&
      customInfo.customId &&
      customInfo.emailLogoImgPath
    ) {
      src = GlobalUtils.getFile(
        customInfo.customType,
        customInfo.customId,
        "email_logo",
        customInfo.emailLogoImgPath
      );
    }
    return src;
  };

  return {
    getDefaultCustomInfo,
    onChangeImg,
    validationSchema,
    customValuesToFormData,
    renderImg,
    getDefaultSideBar,
    getDefaultEmail,
    getBackgroundFileLiveSrc,
    getBigLogoLiveSrc,
    getDefaultLoginPage,
    getReportLogoLiveSrc,
    getEmailLogoLiveSrc,
    getEmptyLogoLoginImageMap,
    getEmptyBackgroundLoginImageMap,
    getEmptyLogoOnHeaderBar,
    getEmptyLogoInEmail,
    getEmptyReportLogo,
    getEmptyFavicon,
    getHomeLogoImgSrc,
    getFaviconImgSrc,
    getDefaultHomeSideBar,
    getAdminLogoImgSrc,
    getEmptyAdminLogo,
    getDefaultAdminSideBar,
    mergeDataWithValueDefault,
  };
};
